import {
  Editor,
  Transforms,
  Element as SlateElement,
  Node,
  Range,
  Path
} from 'slate';

import { ReactEditor } from 'slate-react';

import * as serializerUtilities from 'basic-editor-value-serializer';

import { toUnique } from 'utilities/collection';

import elementTypes from '../../elements/types';
import { toggleBlock } from '../../3rd-party/rich-text';

import HelptapEditor from '../';

function tryConvertCurrentNodeToBulletList(editor, event, wordStat) {

  if (wordStat.leafFocus.offset !== 1) {
    return;
  }

  const targetNodePath = HelptapEditor.getTargetBlockNodePath(editor, wordStat.leafFocus.path);

  if (targetNodePath === null) {
    return;
  }

  const node = Node.get(editor, targetNodePath);

  if (node.type === elementTypes.BULLETED_LIST) {
    return;
  }

  event.preventDefault();

  toggleBlock(editor, elementTypes.BULLETED_LIST);
  const textNodePath = [...targetNodePath, 0, 0];
  Transforms.delete(editor, {
    at: {
      anchor: { path: textNodePath, offset: 0 },
      focus: { path: textNodePath, offset: 1 }
    }
  });

}

function tryConvertCurrentNodeToNumberList(editor, event, wordStat) {

  if (wordStat.leafFocus.offset !== 2) {
    return;
  }

  const targetNodePath = HelptapEditor.getTargetBlockNodePath(editor, wordStat.leafFocus.path);

  if (targetNodePath === null) {
    return;
  }

  const node = Node.get(editor, targetNodePath);

  if (node.type === elementTypes.NUMBERED_LIST) {
    return;
  }

  event.preventDefault();

  Editor.withoutNormalizing(editor, () => {

    if (Path.hasPrevious(targetNodePath) === true) {

      const previousNodePath = Path.previous(targetNodePath);
      const previousNode = Node.get(editor, previousNodePath);

      if (previousNode.type === elementTypes.NUMBERED_LIST) {
        const listItemNode = HelptapEditor.list.buildListItemNode(elementTypes.NUMBERED_LIST_ITEM);
        Transforms.insertNodes(editor, listItemNode, { at: [...previousNodePath, previousNode.children.length] });
        Transforms.select(editor, { path: [...previousNodePath, previousNode.children.length, 0], offset: 0 });
        Transforms.delete(editor, { at: targetNodePath });
        return;
      }

    }

    toggleBlock(editor, elementTypes.NUMBERED_LIST);
    const textNodePath = [...targetNodePath, 0, 0];
    Transforms.delete(editor, {
      at: {
        anchor: { path: textNodePath, offset: 0 },
        focus: { path: textNodePath, offset: 2 }
      }
    });

  });

}

function tryConvertCurrentNodeToCheckList(editor, event, wordStat) {

  if (wordStat.leafFocus.offset !== 2) {
    return;
  }

  const targetNodePath = HelptapEditor.getTargetBlockNodePath(editor, wordStat.leafFocus.path);

  if (targetNodePath === null) {
    return;
  }

  const node = Node.get(editor, targetNodePath);

  if (node.type === elementTypes.CHECK_LIST_ITEM) {
    return;
  }

  const togglableTypes = [
    elementTypes.BLOCK_QUOTE, elementTypes.HEADING_ONE,
    elementTypes.HEADING_TWO, elementTypes.HEADING_THREE,
    elementTypes.HEADING_FOUR, elementTypes.HEADING_FIVE,
    elementTypes.DEFAULT
  ];
  const splitableTypes = [
    elementTypes.BULLETED_LIST_ITEM,
    elementTypes.NUMBERED_LIST_ITEM
  ];

  if (togglableTypes.indexOf(node.type) !== -1) {

    event.preventDefault();

    toggleBlock(editor, elementTypes.CHECK_LIST_ITEM);
    const textNodePath = [...targetNodePath, 0];

    Transforms.delete(editor, {
      at: {
        anchor: { path: textNodePath, offset: 0 },
        focus: { path: textNodePath, offset: 2 }
      }
    });

  } else if (splitableTypes.indexOf(node.type) !== -1) {

  }

}

function tryConvertCurrentNodeToToggleList(editor, event, wordStat) {

  if (wordStat.start !== 0) {
    return;
  }

  const targetNodePath = HelptapEditor.getTargetBlockNodePath(editor, wordStat.leafFocus.path);

  if (targetNodePath === null) {
    return;
  }

  const node = Node.get(editor, targetNodePath);

  if (node.type === elementTypes.TOGGLE_LIST_HEAD) {
    return;
  }
  event.preventDefault();
  HelptapEditor.toggleListItem.toggle(editor, {}, {}, targetNodePath);

}

export function tryConvertCurrentNode(editor, event, wordStat, disabledElements) {

  if (wordStat.word === '*') {
    tryConvertCurrentNodeToBulletList(editor, event, wordStat);
  } else if (wordStat.word === '1.') {
    tryConvertCurrentNodeToNumberList(editor, event, wordStat);
  } else if (disabledElements.indexOf(elementTypes.TOGGLE_LIST_ITEM) === -1 && wordStat.word === '>') {
    tryConvertCurrentNodeToToggleList(editor, event, wordStat);
  } else if (wordStat.word === '[]') {
    tryConvertCurrentNodeToCheckList(editor, event, wordStat);
  }

}

export function canConvertToAnotherType(editor, dragHandlePath) {

  const targetPaths = determineTargetPaths(editor, dragHandlePath);

  if (targetPaths.length === 0) {
    return false;
  }

  for (let path of targetPaths) {
    const node = Node.get(editor, path);
    const transitions = getTransitions(node);
    if (transitions.length === 0) {
      return false;
    }
  }

  return true;

}

export function getTransitions(node) {

  return serializerUtilities.transitions[node.type] || [];

}

function getCommonTransitions(nodeTransitions) {

  const transitionsCounter = {};

  for (let transitions of nodeTransitions) {

    for (let transition of transitions) {

      if (transitionsCounter[transition.type] === void 0) {
        transitionsCounter[transition.type] = { transition, count: 0 };
      }

      transitionsCounter[transition.type].count++;

    }

  }

  const commonTransitions = [];

  for (let transitionType in transitionsCounter) {

    if (transitionsCounter[transitionType].count === nodeTransitions.length) {
      commonTransitions.push(transitionsCounter[transitionType].transition);
    }

  }

  return commonTransitions;

}

export function getCompatibleTransitions(editor, dragHandlePath) {

  const targetPaths = determineTargetPaths(editor, dragHandlePath);

  const nodeTransitions = [];

  for (let path of targetPaths) {
    const node = Node.get(editor, path);
    const transitions = getTransitions(node);
    nodeTransitions.push(transitions);
  }

  return getCommonTransitions(nodeTransitions);

}

function convertListToAnotherList(editor, targetPath, targetType, newType, newChildrenType) {

  const [matchedList] = Editor.nodes(editor, {
    at: targetPath,
    match: node => SlateElement.isElement(node) && node.type === targetType,
  });

  if (matchedList) {
    const [node, path] = matchedList;

    // Change the list type to bulleted-list
    Transforms.setNodes(
      editor, { type: newType }, { at: path }
    );

    // Update all children from numbered-list-item to bulleted-list-item
    for (let i = 0; i < node.children.length; i++) {
      const itemPath = [...path, i];
      Transforms.setNodes(
        editor, { type: newChildrenType }, { at: itemPath }
      );
    }
  }

}

function convertNumberListToBulletList(editor, targetPath) {
  convertListToAnotherList(editor, targetPath, elementTypes.NUMBERED_LIST, elementTypes.BULLETED_LIST, elementTypes.BULLETED_LIST_ITEM);
}

function convertListToBlock(editor, targetPath, targetType, newType) {

  const [matchedList] = Editor.nodes(editor, {
    at: targetPath,
    match: node => SlateElement.isElement(node) && node.type === targetType,
  });

  if (matchedList) {
    const [node, path] = matchedList;

    // Convert all children to check-list-item
    for (let i = 0; i < node.children.length; i++) {
      const itemPath = [...path, i];
      Transforms.setNodes(
        editor, { type: newType }, { at: itemPath }
      );
    }
    // Unwrap the numbered list
    Transforms.unwrapNodes(editor, { at: path });

  }

}

function convertNumberListToCheckList(editor, targetPath) {

  convertListToBlock(editor, targetPath, elementTypes.NUMBERED_LIST, elementTypes.CHECK_LIST_ITEM);

}

function convertNumberListToDefault(editor, targetPath) {

  convertListToBlock(editor, targetPath, elementTypes.NUMBERED_LIST, elementTypes.DEFAULT);

}

function convertBulletListToNumberList(editor, targetPath) {
  convertListToAnotherList(editor, targetPath, elementTypes.BULLETED_LIST, elementTypes.NUMBERED_LIST, elementTypes.NUMBERED_LIST_ITEM);
}

function convertBulletListToCheckList(editor, targetPath) {

  convertListToBlock(editor, targetPath, elementTypes.BULLETED_LIST, elementTypes.CHECK_LIST_ITEM);

}

function convertBulletListToDefault(editor, targetPath) {

  convertListToBlock(editor, targetPath, elementTypes.BULLETED_LIST, elementTypes.DEFAULT);

}

function convertBlockToList(editor, targetPath, targetType, newType, newChildrenType) {

  const checkListItems = Editor.nodes(editor, {
    at: targetPath,
    match: node => SlateElement.isElement(node) && node.type === targetType,
  });

  const pathsToWrap = [];

  for (const [, path] of checkListItems) {
    pathsToWrap.push(path);
    // Convert each check-list-item to numbered-list-item
    Transforms.setNodes(
      editor, { type: newChildrenType }, { at: path }
    );
  }

  if (pathsToWrap.length > 0) {
    const firstPath = pathsToWrap[0];
    const lastPath = pathsToWrap[pathsToWrap.length - 1];

    Transforms.wrapNodes(
      editor, { type: newType, children: [] }, { at: { anchor: Editor.start(editor, firstPath), focus: Editor.end(editor, lastPath) } }
    );
  }

}

function convertCheckListToBulletList(editor, targetPath) {

  convertBlockToList(editor, targetPath, elementTypes.CHECK_LIST_ITEM, elementTypes.BULLETED_LIST, elementTypes.BULLETED_LIST_ITEM);

}

function convertCheckListToNumberList(editor, targetPath) {

  convertBlockToList(editor, targetPath, elementTypes.CHECK_LIST_ITEM, elementTypes.NUMBERED_LIST, elementTypes.NUMBERED_LIST_ITEM);

}

function convertBlockToAnotherBlock(editor, targetPath, targetType, newType, properties = {}) {

  const checkListItems = Editor.nodes(editor, {
    at: targetPath,
    match: node => SlateElement.isElement(node) && node.type === targetType,
  });

  for (const [, path] of checkListItems) {
    Transforms.setNodes(
      editor, { type: newType, ...properties }, { at: path }
    );
  }

}

function convertCheckListToDefault(editor, targetPath) {

  convertBlockToAnotherBlock(editor, targetPath, elementTypes.CHECK_LIST_ITEM, elementTypes.DEFAULT);

}

function convertDefaultToBulletList(editor, targetPath) {

  convertBlockToList(editor, targetPath, elementTypes.DEFAULT, elementTypes.BULLETED_LIST, elementTypes.BULLETED_LIST_ITEM);

}

function convertDefaultToNumberList(editor, targetPath) {

  convertBlockToList(editor, targetPath, elementTypes.DEFAULT, elementTypes.NUMBERED_LIST, elementTypes.NUMBERED_LIST_ITEM);

}

function convertDefaultToCheckList(editor, targetPath) {

  convertBlockToAnotherBlock(editor, targetPath, elementTypes.DEFAULT, elementTypes.CHECK_LIST_ITEM);

}

function convertBlockQuoteToBulletList(editor, targetPath) {

  convertBlockToList(editor, targetPath, elementTypes.BLOCK_QUOTE, elementTypes.BULLETED_LIST, elementTypes.BULLETED_LIST_ITEM);

}

function convertBlockQuoteToNumberList(editor, targetPath) {

  convertBlockToList(editor, targetPath, elementTypes.BLOCK_QUOTE, elementTypes.NUMBERED_LIST, elementTypes.NUMBERED_LIST_ITEM);

}

function convertBlockQuoteToCheckList(editor, targetPath) {

  convertBlockToAnotherBlock(editor, targetPath, elementTypes.BLOCK_QUOTE, elementTypes.CHECK_LIST_ITEM);

}

function convertBlockQuoteToDefault(editor, targetPath) {

  convertBlockToAnotherBlock(editor, targetPath, elementTypes.BLOCK_QUOTE, elementTypes.DEFAULT);

}

function convertHeadingOneToBulletList(editor, targetPath) {

  convertBlockToList(editor, targetPath, elementTypes.HEADING_ONE, elementTypes.BULLETED_LIST, elementTypes.BULLETED_LIST_ITEM);

}

function convertHeadingOneToNumberList(editor, targetPath) {

  convertBlockToList(editor, targetPath, elementTypes.HEADING_ONE, elementTypes.NUMBERED_LIST, elementTypes.NUMBERED_LIST_ITEM);

}

function convertHeadingOneToCheckList(editor, targetPath) {

  convertBlockToAnotherBlock(editor, targetPath, elementTypes.HEADING_ONE, elementTypes.CHECK_LIST_ITEM);

}

function convertHeadingOneToDefault(editor, targetPath) {

  convertBlockToAnotherBlock(editor, targetPath, elementTypes.HEADING_ONE, elementTypes.DEFAULT);

}

function convertHeadingTwoToBulletList(editor, targetPath) {

  convertBlockToList(editor, targetPath, elementTypes.HEADING_TWO, elementTypes.BULLETED_LIST, elementTypes.BULLETED_LIST_ITEM);

}

function convertHeadingTwoToNumberList(editor, targetPath) {

  convertBlockToList(editor, targetPath, elementTypes.HEADING_TWO, elementTypes.NUMBERED_LIST, elementTypes.NUMBERED_LIST_ITEM);

}

function convertHeadingTwoToCheckList(editor, targetPath) {

  convertBlockToAnotherBlock(editor, targetPath, elementTypes.HEADING_TWO, elementTypes.CHECK_LIST_ITEM);

}

function convertHeadingTwoToDefault(editor, targetPath) {

  convertBlockToAnotherBlock(editor, targetPath, elementTypes.HEADING_TWO, elementTypes.DEFAULT);

}

function convertHeadingThreeToBulletList(editor, targetPath) {

  convertBlockToList(editor, targetPath, elementTypes.HEADING_THREE, elementTypes.BULLETED_LIST, elementTypes.BULLETED_LIST_ITEM);

}

function convertHeadingThreeToNumberList(editor, targetPath) {

  convertBlockToList(editor, targetPath, elementTypes.HEADING_THREE, elementTypes.NUMBERED_LIST, elementTypes.NUMBERED_LIST_ITEM);

}

function convertHeadingThreeToCheckList(editor, targetPath) {

  convertBlockToAnotherBlock(editor, targetPath, elementTypes.HEADING_THREE, elementTypes.CHECK_LIST_ITEM);

}

function convertHeadingThreeToDefault(editor, targetPath) {

  convertBlockToAnotherBlock(editor, targetPath, elementTypes.HEADING_THREE, elementTypes.DEFAULT);

}

function convertHeadingFourToBulletList(editor, targetPath) {

  convertBlockToList(editor, targetPath, elementTypes.HEADING_FOUR, elementTypes.BULLETED_LIST, elementTypes.BULLETED_LIST_ITEM);

}

function convertHeadingFourToNumberList(editor, targetPath) {

  convertBlockToList(editor, targetPath, elementTypes.HEADING_FOUR, elementTypes.NUMBERED_LIST, elementTypes.NUMBERED_LIST_ITEM);

}

function convertHeadingFourToCheckList(editor, targetPath) {

  convertBlockToAnotherBlock(editor, targetPath, elementTypes.HEADING_FOUR, elementTypes.CHECK_LIST_ITEM);

}

function convertHeadingFourToDefault(editor, targetPath) {

  convertBlockToAnotherBlock(editor, targetPath, elementTypes.HEADING_FOUR, elementTypes.DEFAULT);

}

function convertHeadingFiveToBulletList(editor, targetPath) {

  convertBlockToList(editor, targetPath, elementTypes.HEADING_FIVE, elementTypes.BULLETED_LIST, elementTypes.BULLETED_LIST_ITEM);

}

function convertHeadingFiveToNumberList(editor, targetPath) {

  convertBlockToList(editor, targetPath, elementTypes.HEADING_FIVE, elementTypes.NUMBERED_LIST, elementTypes.NUMBERED_LIST_ITEM);

}

function convertHeadingFiveToCheckList(editor, targetPath) {

  convertBlockToAnotherBlock(editor, targetPath, elementTypes.HEADING_FIVE, elementTypes.CHECK_LIST_ITEM);

}

function convertHeadingFiveToDefault(editor, targetPath) {

  convertBlockToAnotherBlock(editor, targetPath, elementTypes.HEADING_FIVE, elementTypes.DEFAULT);

}

function convertHeadingSixToBulletList(editor, targetPath) {

  convertBlockToList(editor, targetPath, elementTypes.HEADING_SIX, elementTypes.BULLETED_LIST, elementTypes.BULLETED_LIST_ITEM);

}

function convertHeadingSixToNumberList(editor, targetPath) {

  convertBlockToList(editor, targetPath, elementTypes.HEADING_SIX, elementTypes.NUMBERED_LIST, elementTypes.NUMBERED_LIST_ITEM);

}

function convertHeadingSixToCheckList(editor, targetPath) {

  convertBlockToAnotherBlock(editor, targetPath, elementTypes.HEADING_SIX, elementTypes.CHECK_LIST_ITEM);

}

function convertHeadingSixToDefault(editor, targetPath) {

  convertBlockToAnotherBlock(editor, targetPath, elementTypes.HEADING_SIX, elementTypes.DEFAULT);

}

function convertButtonToDefault(editor, targetPath) {

  convertBlockToAnotherBlock(editor, targetPath, elementTypes.BUTTON, elementTypes.DEFAULT, {
    ...HelptapEditor.paragraph.buildNode(),
    backgroundColor: '',
    alignment: '',
    textColor: ''
  });

}

function convertDefaultToButton(editor, targetPath) {

  convertBlockToAnotherBlock(editor, targetPath, elementTypes.DEFAULT, elementTypes.BUTTON, HelptapEditor.button.getDefaultOptions());

}

function getConvertors() {

  const convertors = {
    [`${elementTypes.NUMBERED_LIST}->${elementTypes.BULLETED_LIST}`]: convertNumberListToBulletList,
    [`${elementTypes.NUMBERED_LIST}->${elementTypes.CHECK_LIST_ITEM}`]: convertNumberListToCheckList,
    [`${elementTypes.NUMBERED_LIST}->${elementTypes.DEFAULT}`]: convertNumberListToDefault,
    [`${elementTypes.BULLETED_LIST}->${elementTypes.NUMBERED_LIST}`]: convertBulletListToNumberList,
    [`${elementTypes.BULLETED_LIST}->${elementTypes.CHECK_LIST_ITEM}`]: convertBulletListToCheckList,
    [`${elementTypes.BULLETED_LIST}->${elementTypes.DEFAULT}`]: convertBulletListToDefault,
    [`${elementTypes.CHECK_LIST_ITEM}->${elementTypes.BULLETED_LIST}`]: convertCheckListToBulletList,
    [`${elementTypes.CHECK_LIST_ITEM}->${elementTypes.NUMBERED_LIST}`]: convertCheckListToNumberList,
    [`${elementTypes.CHECK_LIST_ITEM}->${elementTypes.DEFAULT}`]: convertCheckListToDefault,
    [`${elementTypes.DEFAULT}->${elementTypes.BULLETED_LIST}`]: convertDefaultToBulletList,
    [`${elementTypes.DEFAULT}->${elementTypes.NUMBERED_LIST}`]: convertDefaultToNumberList,
    [`${elementTypes.DEFAULT}->${elementTypes.CHECK_LIST_ITEM}`]: convertDefaultToCheckList,
    [`${elementTypes.DEFAULT}->${elementTypes.BUTTON}`]: convertDefaultToButton,
    [`${elementTypes.BLOCK_QUOTE}->${elementTypes.BULLETED_LIST}`]: convertBlockQuoteToBulletList,
    [`${elementTypes.BLOCK_QUOTE}->${elementTypes.NUMBERED_LIST}`]: convertBlockQuoteToNumberList,
    [`${elementTypes.BLOCK_QUOTE}->${elementTypes.CHECK_LIST_ITEM}`]: convertBlockQuoteToCheckList,
    [`${elementTypes.BLOCK_QUOTE}->${elementTypes.DEFAULT}`]: convertBlockQuoteToDefault,
    [`${elementTypes.HEADING_ONE}->${elementTypes.BULLETED_LIST}`]: convertHeadingOneToBulletList,
    [`${elementTypes.HEADING_ONE}->${elementTypes.NUMBERED_LIST}`]: convertHeadingOneToNumberList,
    [`${elementTypes.HEADING_ONE}->${elementTypes.CHECK_LIST_ITEM}`]: convertHeadingOneToCheckList,
    [`${elementTypes.HEADING_ONE}->${elementTypes.DEFAULT}`]: convertHeadingOneToDefault,
    [`${elementTypes.HEADING_TWO}->${elementTypes.BULLETED_LIST}`]: convertHeadingTwoToBulletList,
    [`${elementTypes.HEADING_TWO}->${elementTypes.NUMBERED_LIST}`]: convertHeadingTwoToNumberList,
    [`${elementTypes.HEADING_TWO}->${elementTypes.CHECK_LIST_ITEM}`]: convertHeadingTwoToCheckList,
    [`${elementTypes.HEADING_TWO}->${elementTypes.DEFAULT}`]: convertHeadingTwoToDefault,
    [`${elementTypes.HEADING_THREE}->${elementTypes.BULLETED_LIST}`]: convertHeadingThreeToBulletList,
    [`${elementTypes.HEADING_THREE}->${elementTypes.NUMBERED_LIST}`]: convertHeadingThreeToNumberList,
    [`${elementTypes.HEADING_THREE}->${elementTypes.CHECK_LIST_ITEM}`]: convertHeadingThreeToCheckList,
    [`${elementTypes.HEADING_THREE}->${elementTypes.DEFAULT}`]: convertHeadingThreeToDefault,
    [`${elementTypes.HEADING_FOUR}->${elementTypes.BULLETED_LIST}`]: convertHeadingFourToBulletList,
    [`${elementTypes.HEADING_FOUR}->${elementTypes.NUMBERED_LIST}`]: convertHeadingFourToNumberList,
    [`${elementTypes.HEADING_FOUR}->${elementTypes.CHECK_LIST_ITEM}`]: convertHeadingFourToCheckList,
    [`${elementTypes.HEADING_FOUR}->${elementTypes.DEFAULT}`]: convertHeadingFourToDefault,
    [`${elementTypes.HEADING_FIVE}->${elementTypes.BULLETED_LIST}`]: convertHeadingFiveToBulletList,
    [`${elementTypes.HEADING_FIVE}->${elementTypes.NUMBERED_LIST}`]: convertHeadingFiveToNumberList,
    [`${elementTypes.HEADING_FIVE}->${elementTypes.CHECK_LIST_ITEM}`]: convertHeadingFiveToCheckList,
    [`${elementTypes.HEADING_FIVE}->${elementTypes.DEFAULT}`]: convertHeadingFiveToDefault,
    [`${elementTypes.HEADING_SIX}->${elementTypes.BULLETED_LIST}`]: convertHeadingSixToBulletList,
    [`${elementTypes.HEADING_SIX}->${elementTypes.NUMBERED_LIST}`]: convertHeadingSixToNumberList,
    [`${elementTypes.HEADING_SIX}->${elementTypes.CHECK_LIST_ITEM}`]: convertHeadingSixToCheckList,
    [`${elementTypes.HEADING_SIX}->${elementTypes.DEFAULT}`]: convertHeadingSixToDefault,
    [`${elementTypes.BUTTON}->${elementTypes.DEFAULT}`]: convertButtonToDefault
  };

  return convertors;

}

/**
 * Get all paths at a specific depth within a given range.
 * @param {Range} range - The range object containing start and end.
 * @param {number} depth - The depth level for which to collect paths.
 * @returns {Array<Array<number>>} - An array of paths at the specified depth within the range.
 */
function getPathsInRangeAtDepth(range, depth) {
  const paths = [];

  // Extract start and end paths from the range
  const { anchor, focus } = range;

  // Get the starting and ending indices for the specified depth
  const startDepthPath = anchor.path.slice(0, depth);
  const endDepthPath = focus.path.slice(0, depth);

  // Determine the range of indices for the specified depth
  const startIdx = startDepthPath[depth - 1];
  const endIdx = endDepthPath[depth - 1];

  // Collect all paths at the specified depth within the range
  for (let i = startIdx; i <= endIdx; i++) {
    const path = [...startDepthPath.slice(0, depth - 1), i]; // Construct the path
    paths.push(path);
  }

  return paths;
};

function determineTargetLocation(editor, path) {

  const { selection } = editor;

  if (!selection || Range.includes(selection, path) === false) {
    return { anchor: { path }, focus: { path } };
  }

  return selection;

}

export function convertNodeV1(editor, node, type, path) {

  const convertors = getConvertors();
  const convertor = convertors[`${node.type}->${type}`];

  if (convertor) {
    const targetLocation = determineTargetLocation(editor, path);
    convertor(editor, node, targetLocation);
  }

  ReactEditor.focus(editor);

}

export function determineTargetPaths(editor, dragHandlePath) {
  const draggableTypes = serializerUtilities.draggableElements; // Define your draggable node types here

  // Helper function: Check if the node at a given path is draggable
  function isDraggable(path) {
    const node = Node.get(editor, path);
    return draggableTypes.includes(node.type);
  }

  // Helper function: Get the bottom-level draggable path in the hierarchy
  function getBottomLevelDraggablePath(path) {
    let currentPath = path;

    // Traverse upwards until we find a draggable node
    while (currentPath.length > 0) {
      if (isDraggable(currentPath)) {
        return currentPath;
      }
      currentPath = Path.parent(currentPath);
    }

    return null; // No draggable node found
  }

  // Helper function: Collect the lowest draggable paths from a selection range
  function getSelectedDraggablePaths(selection) {
    const selectedPaths = [];
    const matchingNodesGenerator = Editor.nodes(editor, {
      at: selection,
      mode: 'lowest',
      match(node) {
        return draggableTypes.includes(node.type);
      }
    });
    for (const [, path] of matchingNodesGenerator) {
      const bottomLevelPath = getBottomLevelDraggablePath(path);
      if (bottomLevelPath) {
        selectedPaths.push(bottomLevelPath);
      }
    }

    return selectedPaths;
  }

  // Case 1: Check if there's an active selection in the editor
  if (editor.selection && Range.isExpanded(editor.selection)) {
    const selectedDraggablePaths = getSelectedDraggablePaths(editor.selection);

    // If the drag handle path is part of the selection, return the selected draggable paths
    if (selectedDraggablePaths.some(path => Path.equals(path, dragHandlePath))) {
      return toUnique(selectedDraggablePaths);
    }
  }

  // Case 2: If drag handle path is not part of the selection, use it
  const draggableHandlePath = getBottomLevelDraggablePath(dragHandlePath);

  if (draggableHandlePath) {
    return [draggableHandlePath];
  }

  // Fallback: If no draggable path is found, return an empty array
  return [];
}

export function convertNode(editor, node, type, path) {

  // const selectionRef = Editor.rangeRef(editor, targetRange, {
  //   affinity: 'inward',
  // });

  Editor.withoutNormalizing(editor, () => {
    const targetNodesPath = determineTargetPaths(editor, path).reverse();
    for (let targetPath of targetNodesPath) {
      const targetNode = Node.get(editor, targetPath);
      const convertors = getConvertors();
      const convertor = convertors[`${targetNode.type}->${type}`];
      if (convertor) {
        convertor(editor, targetPath, type);
      }
    }

  });
  // Transforms.select(editor, selectionRef.unref());
}